<script setup lang="ts">
import { computed } from 'vue';
import { isBoolean, isNil } from 'lodash';
import { t } from '@nova/core';

import type { CustomFieldProps } from '../types';
import CustomFieldInputBase from '../CustomFieldInputBase.vue';
import { useCustomFieldProps } from '@satellite/composables/useCustomFieldProps';
import { useCustomFieldValidation } from '@satellite/composables/useCustomFieldValidation';

/**
 * Boolean field type - a Yes / No button toggle.
 *
 * An unanswered field shows neither button selected. A switch could not show that state: it read
 * "No" for a field that had no value, so a required field looked answered while its validation
 * still refused it.
 */
const {
  hideDetails = 'auto',
  displayLabel = true,
  required = undefined,
  errorMessages = undefined,
  ...props
} = defineProps<
  CustomFieldProps & {
    /**
     * Messages shown instead of the field's own rule results. For a host that validates outside
     * Vuetify's form, such as Luna's BasicForm, and reports the outcome back here.
     */
    errorMessages?: string[];
  }
>();

const emit = defineEmits<{
  'update:model-value': [value: boolean | null];
  /** @deprecated Use update:model-value instead */
  input: [value: boolean | null];
}>();

const { normalizedProps } = useCustomFieldProps(() => ({
  ...props,
  hideDetails,
  displayLabel,
  required
}));
const { rules } = useCustomFieldValidation(
  computed(() => ({ ...props.field, required: normalizedProps.required }))
);

/** Each option carries the field's test id plus a fixed suffix, as a timestamp's controls do. */
const yesTestId = computed(() => (props.testId ? `${props.testId}-yes` : undefined));
const noTestId = computed(() => (props.testId ? `${props.testId}-no` : undefined));

/**
 * The toggle types its emit after modelValue, which is shared by every field type. Its buttons
 * emit true and false, and clearing an optional field emits undefined, which is reported as null:
 * the value an unanswered field holds everywhere else.
 */
function handleInput(value: CustomFieldProps['modelValue'] | undefined) {
  if (!isBoolean(value) && !isNil(value)) {
    return;
  }
  const answer = value ?? null;
  emit('update:model-value', answer);
  emit('input', answer);
}
</script>

<template>
  <custom-field-input-base :field="field" v-bind="normalizedProps">
    <!--
      v-btn-toggle is not a form input: it takes no rules and never registers with the
      surrounding v-form, so a required field would save empty. The v-input carries the
      validation and its message, and the toggle only handles the choice.

      `mandatory` applies to required fields only: once answered, clicking the selected option
      cannot clear it and raise the validation error out of nowhere. An optional field can be
      cleared back to unanswered that way. The "force" variant would select Yes on mount and
      answer the question for the user.

      Vuetify renders no role or pressed state on the toggle, so both are set here: the switch
      this replaced exposed its state through aria-checked, and the field label cannot name a
      group of buttons through its `for`.
    -->
    <v-input
      :hide-details="normalizedProps.hideDetails"
      :model-value="modelValue"
      :rules="rules"
      :error-messages="errorMessages">
      <v-btn-toggle
        :data-testid="testId"
        role="group"
        :aria-label="field.label"
        variant="outlined"
        color="primary"
        density="comfortable"
        :mandatory="normalizedProps.required"
        :disabled="normalizedProps.readOnly"
        :model-value="modelValue"
        @update:model-value="handleInput">
        <v-btn :value="true" :aria-pressed="modelValue === true" :data-testid="yesTestId">
          {{ t('global.basicTerms.yes') }}
        </v-btn>
        <v-btn :value="false" :aria-pressed="modelValue === false" :data-testid="noTestId">
          {{ t('global.basicTerms.no') }}
        </v-btn>
      </v-btn-toggle>
    </v-input>
  </custom-field-input-base>
</template>
